import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Mail, Loader2, Eye } from "lucide-react";

const TYPE_LABELS = {
  proposal_sent: "Envoi de la proposition (au prospect)",
  follow_up: "Relance 48 h (au prospect)",
  question: "Question reçue (notification interne)",
  decision: "Décision — accepté/refusé (notification interne)",
  invitation: "Invitation d'un membre",
};

const TYPE_ORDER = ["proposal_sent", "follow_up", "question", "decision", "invitation"];

function TemplateCard({ tpl }) {
  const [subject, setSubject] = useState(tpl.subject || "");
  const [body, setBody] = useState(tpl.body || "");
  const [saving, setSaving] = useState(false);
  const bodyRef = useRef(null);

  const label = TYPE_LABELS[tpl.type] || tpl.type;

  const insertVar = (v) => {
    const el = bodyRef.current;
    const token = `{{${v}}}`;
    if (!el) { setBody((b) => b + token); return; }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = body.slice(0, start) + token + body.slice(end);
    setBody(next);
    // repositionne le curseur après le token inséré
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/email-templates/${tpl.type}`, { subject, body });
      toast.success("Modèle enregistré");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.response?.data?.message || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-card border border-border rounded-sm p-6 lg:p-8 space-y-5" data-testid={`admin-email-${tpl.type}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-primary" />
          <h3 className="font-heading text-lg font-semibold">{label}</h3>
        </div>
        {tpl.pixel && (
          <span className="text-[10px] tracking-wide uppercase px-2 py-1 rounded-sm font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1" data-testid={`admin-email-pixel-${tpl.type}`}>
            <Eye className="w-3 h-3" /> Suivi d'ouverture inclus
          </span>
        )}
      </div>

      <div>
        <Label>Objet</Label>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)}
          data-testid={`admin-email-subject-${tpl.type}`} placeholder="Objet de l'email" className="mt-1.5 rounded-sm" />
      </div>

      <div>
        <Label>Corps</Label>
        <textarea ref={bodyRef} value={body} onChange={(e) => setBody(e.target.value)}
          data-testid={`admin-email-body-${tpl.type}`} rows={10}
          className="mt-1.5 w-full rounded-sm border border-border bg-background px-3 py-2 text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary" />
      </div>

      {tpl.variables?.length > 0 && (
        <div>
          <p className="text-xs text-muted-foreground mb-2">Cliquez pour insérer une variable dans le corps :</p>
          <div className="flex flex-wrap gap-2" data-testid={`admin-email-vars-${tpl.type}`}>
            {tpl.variables.map((v) => (
              <button key={v} type="button" onClick={() => insertVar(v)}
                data-testid={`admin-email-var-${tpl.type}-${v}`}
                className="text-xs font-mono px-2.5 py-1 rounded-sm border border-border bg-muted text-muted-foreground hover:bg-primary hover:text-white hover:border-primary transition-colors">
                {`{{${v}}}`}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="pt-1">
        <Button onClick={save} disabled={saving} data-testid={`admin-email-save-${tpl.type}`} className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
          {saving ? "Enregistrement…" : "Enregistrer"}
        </Button>
      </div>
    </div>
  );
}

export default function AdminEmails() {
  const [templates, setTemplates] = useState(null);

  useEffect(() => {
    api.get("/admin/email-templates")
      .then((r) => {
        const sorted = [...(r.data || [])].sort((a, b) => TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type));
        setTemplates(sorted);
      })
      .catch((e) => { toast.error(e.response?.data?.detail || e.response?.data?.message || "Erreur de chargement"); setTemplates([]); });
  }, []);

  return (
    <div className="max-w-3xl space-y-8" data-testid="admin-emails-page">
      <div>
        <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">Modèles d'email</h1>
        <p className="text-muted-foreground mt-2">Modèles par défaut envoyés par <strong>toute la plateforme</strong>. Chaque société peut surcharger ces modèles avec ses propres textes dans ses Paramètres.</p>
      </div>

      {templates === null ? (
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>
      ) : templates.length === 0 ? (
        <p className="text-muted-foreground">Aucun modèle disponible.</p>
      ) : (
        <div className="space-y-8">
          {templates.map((tpl) => <TemplateCard key={tpl.type} tpl={tpl} />)}
        </div>
      )}
    </div>
  );
}
